import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Segmented, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import { ReloadOutlined, CheckOutlined, StopOutlined } from "@ant-design/icons";

import { getRules, approveRule, disableRule, learnForOffice } from "./controller";
import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";

const STATUS_OPTIONS = [
  { label: "Pending review", value: "pending_review" },
  { label: "Approved", value: "approved" },
  { label: "Disabled", value: "disabled" },
  { label: "All", value: "" },
];

const STATUS_COLORS = {
  approved: "green",
  pending_review: "gold",
  disabled: "default",
  draft: "blue",
};

function pct(v) {
  if (v === null || v === undefined) return "—";
  return Math.round(v * 100) + "%";
}

export default function PredeterminationRulesAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending_review");
  const [officeId, setOfficeId] = useState("");

  const load = useCallback(
    async function loadInner() {
      setLoading(true);
      try {
        const res = await getRules(status);
        setRows(res.status >= 200 && res.status < 300 && Array.isArray(res.json) ? res.json : []);
      } catch (e) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [status],
  );

  useEffect(
    function () {
      load();
    },
    [load],
  );

  async function onApprove(id) {
    const res = await approveRule(id);
    if (res.status >= 200 && res.status < 300) {
      message.success("Rule approved & activated.");
      load();
    } else {
      message.error("Approve failed.");
    }
  }

  async function onDisable(id) {
    const res = await disableRule(id);
    if (res.status >= 200 && res.status < 300) {
      message.success("Rule disabled.");
      load();
    } else {
      message.error("Disable failed.");
    }
  }

  async function onLearn() {
    if (!officeId) {
      message.warning("Enter an office id.");
      return;
    }
    const res = await learnForOffice(officeId);
    if (res.status >= 200 && res.status < 300) {
      message.success("Learning enqueued for office " + officeId + ".");
    } else {
      message.error((res.json && res.json.error) || "Could not enqueue learning.");
    }
  }

  const columns = [
    { title: "Office", width: 180, render: (_, r) => <span>{r.office_name || "#" + r.office_id}</span> },
    { title: "Status", dataIndex: "status", width: 130, render: (s) => <Tag color={STATUS_COLORS[s]}>{s}</Tag> },
    {
      title: "Active",
      dataIndex: "is_active",
      width: 80,
      render: (a) => (a ? <Tag color="green">live</Tag> : <span style={{ color: "#bbb" }}>—</span>),
    },
    { title: "Confidence", dataIndex: "confidence", width: 100, render: pct },
    { title: "Sample", dataIndex: "sample_size", width: 80 },
    {
      title: "What it learned",
      dataIndex: "reasoning",
      render: (t) => (
        <Tooltip title={t}>
          <span
            style={{
              display: "inline-block",
              maxWidth: 420,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "bottom",
            }}
          >
            {t || "—"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 210,
      align: "right",
      render: (_, r) => (
        <Space>
          <Button size="small" type="primary" icon={<CheckOutlined />} disabled={r.is_active} onClick={() => onApprove(r.id)}>
            Approve
          </Button>
          <Button size="small" danger icon={<StopOutlined />} disabled={r.status === "disabled"} onClick={() => onDisable(r.id)}>
            Disable
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <DashboardLayout>
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 8 }}>
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Predetermination Rules
        </Typography.Title>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </div>
      <p style={{ color: "rgba(0,0,0,0.45)", margin: "0 0 16px", fontSize: 13 }}>
        Per-office rules the agent learned for spotting predetermination (pre-treatment estimate) submissions in PMS claims. High-confidence
        rules apply automatically; review the rest here.
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: "12px 0 16px" }}>
        <Segmented value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        <div style={{ display: "flex" }}>
          <Input
            placeholder="Office id"
            value={officeId}
            onChange={(e) => setOfficeId(e.target.value.replace(/[^0-9]/g, ""))}
            style={{ width: 120 }}
          />
          <Button onClick={onLearn} style={{ marginLeft: 8 }}>
            Learn now
          </Button>
        </div>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        size="middle"
        expandable={{
          expandedRowRender: (r) => (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <b>Effective rule</b>
                <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{JSON.stringify(r.effective_discriminator, null, 2)}</pre>
              </div>
              <div>
                <b>Validation</b>
                <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{JSON.stringify(r.validation, null, 2)}</pre>
              </div>
            </div>
          ),
        }}
        pagination={{ pageSize: 25, showSizeChanger: false }}
      />
    </div>
    </DashboardLayout>
  );
}
